import React from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
  Outlet
} from 'react-router-dom';
import Navbar from './components/Navbar';
import Usuarios from './pages/Users/Users';
import Proyectos from './pages/Projects/Projects';
import Informes from './pages/Reports/Reports';
import Actividades from './pages/Activities/Activities';
import Equipos from './pages/Equipment/Equipment';
import Personal from './pages/Employee/Employee';
import Subcontracts from './pages/Subcontracts/Subcontracts';
import ReporteDiario from './pages/DailyReport/DailyReport';
import Login from './pages/Login/Login';
import ProductionControl from './pages/Reports/ProductionControl';
import CreateDailyReport from './pages/DailyReport/CreateDailyReport';
import ProtectedRoute from './middleware/ProtectedRoutes';
import AccessDenied from './pages/DeniedAccess/NotAuthorized';
import { profiles } from './contexts/constants/profiles';
import Summary from './pages/Reports/Summary';
import RasReport from './pages/Reports/RasReport';
import MonthlySummary from './pages/Reports/MonthlySummary';
import WeeklyProgramming from './pages/Projects/WeeklyProgramming';
import ChangePasswordPage from "./pages/changePasword/ChangePaswordPage";
import ConsolidatedPerformance from './pages/Reports/ConsolidatedPerformance';
import ConsolidatedUnproductivity from './pages/Reports/ConsolidatedUnproductivity';
import EmployeeCalendar from './pages/Reports/EmployeeCalendar';
import EquipmentCalendar from './pages/Reports/EquipmentCalendar';
import SubcontractCalendar from './pages/Reports/SubcontractCalendar';
import TimeLocationChart from './pages/Reports/TimeLocationChart';
import { useAxiosInterceptors } from './hooks/useAxiosInterceptors';
import { SnackbarProvider } from './contexts/SnackbarContext';
import { CountryProvider } from './contexts/CountryContext';
import SelectCountry from './pages/SelectCountry/SelectCountry';
import Countries from './pages/Countries/Countries';
import ProjectSchedulePage from './pages/Reports/ProjectSchedulePage';
import PowerBiReports from './pages/Reports/PowerBiReports';
import OAuth2Callback from './pages/Login/OAuth2Callback';

const AxiosInterceptorWrapper: React.FC = () => {
  useAxiosInterceptors();
  return null;
};

const RootLayout: React.FC = () => {
  return (
    <>
      <AxiosInterceptorWrapper />
      <Navbar />
      <Outlet />
    </>
  );
};

// 3. Definimos el enrutador con createBrowserRouter
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootLayout />}>

      <Route path="/" element={<Login />} />
      <Route path="/login" element={<Login />} />
      <Route path="/seleccionar-pais" element={<SelectCountry />} />
      <Route path="/recovery/:token" element={<ChangePasswordPage />} />
      <Route path="/auth/callback" element={<OAuth2Callback />} />

      <Route element={<ProtectedRoute allowedRoles={[profiles[0].name, profiles[1].name, profiles[2].name]} />}>
        <Route path="/reporte-diario" element={<ReporteDiario />} />
        <Route path="/reporte-diario/Crear" element={<CreateDailyReport mode="create"/>} />
        <Route path="/reporte-diario/editar/:id" element={<CreateDailyReport mode="edit" />} />
      </Route>

      <Route element={<ProtectedRoute allowedRoles={[profiles[2].name]} />}>
        <Route path="/usuarios" element={<Usuarios />} />
      </Route>

      {/* Administración de Países: SOLO Administrador de Sistema (super-admin) */}
      <Route element={<ProtectedRoute allowedRoles={[profiles[5].name]} />}>
        <Route path="/paises" element={<Countries />} />
      </Route>

      <Route element={<ProtectedRoute allowedRoles={[profiles[0].name, profiles[1].name, profiles[2].name, profiles[4].name]} />}>
        <Route path="/personal" element={<Personal />} />
      </Route>

      <Route element={<ProtectedRoute allowedRoles={[profiles[0].name, profiles[1].name, profiles[2].name, profiles[4].name]} />}>
        <Route path="/equipos" element={<Equipos />} />
      </Route>

      {/* Subcontratos: el Visualizador tiene permiso de lectura segun la matriz del
          documento de reglas de negocio, a diferencia de Personal y Equipos. */}
      <Route element={<ProtectedRoute allowedRoles={[profiles[0].name, profiles[1].name, profiles[2].name, profiles[3].name, profiles[4].name]} />}>
        <Route path="/subcontratos" element={<Subcontracts />} />
      </Route>

      <Route element={<ProtectedRoute allowedRoles={[profiles[2].name]} />}>
        <Route path="/actividades" element={<Actividades />} />
      </Route>

      <Route element={<ProtectedRoute allowedRoles={[profiles[0].name, profiles[2].name]} />}>
        <Route path="/proyectos" element={<Proyectos />} />
        <Route path="/programacion-semanal" element={<WeeklyProgramming />} />
      </Route>

      <Route element={<ProtectedRoute allowedRoles={[profiles[0].name, profiles[1].name, profiles[2].name, profiles[3].name]} />}>
        <Route path="/informes" element={<Informes />} />
        <Route path="/control-produccion" element={<ProductionControl />} />
        <Route path="/RAS" element={<Summary />} />
        {/* La ruta /resumen se conserva para el semanal: cambió la etiqueta del menú, no la URL. */}
        <Route path="/resumen" element={<RasReport />} />
        <Route path="/resumen-mensual" element={<MonthlySummary />} />
        <Route path="/consolidado-rendimiento" element={<ConsolidatedPerformance />} />
        <Route path="/consolidado-improductividad" element={<ConsolidatedUnproductivity />} />
        <Route path="/personal-calendario" element={<EmployeeCalendar />} />
        <Route path="/equipment-calendar" element={<EquipmentCalendar />} />
        <Route path="/subcontratos-calendario" element={<SubcontractCalendar />} />
        <Route path="/espacio-tiempo" element={<TimeLocationChart />} />
        <Route path="/cronograma-planificacion" element={<ProjectSchedulePage />} />
        <Route path="/informes-powerbi" element={<PowerBiReports />} />
      </Route>

      <Route path="/access-denied" element={<AccessDenied />} />
    </Route>
  )
);

const App: React.FC = () => {
  return (
    <SnackbarProvider>
      <CountryProvider>
        <RouterProvider router={router} />
      </CountryProvider>
    </SnackbarProvider>
  );
}

export default App;